// DAS-3249: the tickers in the LLM analysis text open the standard search result.
// Only words that are tickers the user knows (holdings, open alerts, favorites, past
// advice) are linked: the text is full of capitalised words that are not tickers
// (FAIL, ETF, OPEN ALERTS, HOUSE VIEW) and of real tickers that look like words
// (T, ALL, FIX), so a plain "capital letters" rule links nonsense and a ticker-universe
// lookup links OPEN (Opendoor) and VIEW.

// A capitalised word, optionally with an exchange suffix (NUKL.DE, LONN.SW). Not part
// of AT&T / S&P / W&O, a hyphenated word (HIGH-confidence) or an alert number.
const TOKEN = /(?<![A-Za-z0-9&.\-#/_])([A-Z][A-Z0-9]{0,5}(?:\.[A-Z]{1,3})?)(?![A-Za-z0-9&\-_])/g;
const CAPS_WORD_AFTER = /^ ([A-Z]{2,})(?![a-z])/;

// Words the advice writes in capitals as labels (market weather, confidence bands,
// verdicts, actions, common abbreviations) that are also real tickers (CALM = Cal-Maine,
// LOW = Lowe's). They are linked only in the "Name (TICKER)" form.
const LABEL_WORDS = new Set([
  'CALM', 'NERVOUS', 'PANIC', 'LOW', 'MEDIUM', 'HIGH', 'PASS', 'FAIL', 'BUY', 'SELL', 'HOLD',
  'SWAP', 'TRIM', 'YES', 'NO', 'NOT', 'AI', 'EU', 'US', 'USA', 'UK', 'EM', 'ETF', 'GDP',
  'CEO', 'EPS', 'YTD', 'USD', 'EUR',
]);

// lists: arrays of ticker strings (null/empty entries are ignored). The alert table stores
// some tickers in lower case ('mu'), so the index is upper case.
export function buildTickerIndex(...lists) {
  const exact = new Set();
  lists.flat().forEach((t) => {
    if (typeof t === 'string' && t.trim()) exact.add(t.trim().toUpperCase());
  });
  // The text often names a European listing without its suffix (NUKL for NUKL.DE).
  // Map the bare name only when exactly one known ticker has it.
  const byBase = new Map();
  exact.forEach((t) => {
    const dot = t.indexOf('.');
    if (dot > 0) {
      const base = t.slice(0, dot);
      byBase.set(base, byBase.has(base) ? null : t);
    }
  });
  const base = new Map();
  byBase.forEach((full, b) => { if (full && !exact.has(b)) base.set(b, full); });
  return { exact, base };
}

function resolve(word, index, bracketed = false) {
  if (LABEL_WORDS.has(word) && !bracketed) return null;
  if (index.exact.has(word)) return word;
  return index.base.get(word) || null;
}

// Splits a piece of text into [{ text }, { text, ticker }, ...].
export function splitTickers(text, index) {
  if (!text || !index || (!index.exact.size && !index.base.size)) return [{ text: text || '' }];
  const out = [];
  let last = 0;
  TOKEN.lastIndex = 0;
  let m;
  while ((m = TOKEN.exec(text)) !== null) {
    const word = m[1];
    const after = text.slice(m.index + word.length);
    const ticker = resolve(word, index, text[m.index - 1] === '(' && after[0] === ')');
    if (!ticker) continue;
    // A heading phrase the advice writes in capitals ("BULL LENS", "ALL ALERTS"):
    // the next word is capitalised too and is not a ticker.
    const caps = after.match(CAPS_WORD_AFTER);
    if (caps && !resolve(caps[1], index)) continue;
    // One-letter words: "A trade", "I propose" — a ticker is not followed by a lower-case word.
    if (word.length === 1 && /^ [a-z]/.test(after)) continue;
    if (m.index > last) out.push({ text: text.slice(last, m.index) });
    out.push({ text: word, ticker });
    last = m.index + word.length;
  }
  if (last < text.length) out.push({ text: text.slice(last) });
  return out.length ? out : [{ text }];
}

export const TICKER_HREF = '#ticker=';

// remark plugin (react-markdown remarkPlugins): turns each ticker in a text node into a
// link whose href starts with TICKER_HREF; the page renders those links as clickable
// tickers. Text already inside a link is left alone.
export function remarkTickerLinks(index) {
  const walk = (node) => {
    if (!node || !Array.isArray(node.children) || node.type === 'link' || node.type === 'linkReference') return;
    const next = [];
    node.children.forEach((child) => {
      if (child.type !== 'text') {
        walk(child);
        next.push(child);
        return;
      }
      splitTickers(child.value, index).forEach((part) => {
        next.push(part.ticker
          ? { type: 'link', url: TICKER_HREF + encodeURIComponent(part.ticker), title: null,
              children: [{ type: 'text', value: part.text }] }
          : { type: 'text', value: part.text });
      });
    });
    node.children = next;
  };
  return () => (tree) => { walk(tree); };
}

export function tickerFromHref(href) {
  return typeof href === 'string' && href.startsWith(TICKER_HREF)
    ? decodeURIComponent(href.slice(TICKER_HREF.length))
    : null;
}
